// Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import DoneIcon from '@mui/icons-material/Done'
import ErrorIcon from '@mui/icons-material/Error'
import PendingIcon from '@mui/icons-material/Pending'
import { Alert, Box, Button, CircularProgress, Grid, InputLabel, TextField, Typography } from '@mui/material'
import { theme } from '@react/components/theme'
import { OtStoreState, useOtSelector } from '@react/redux/store'
import { parseLocaleNumber } from '@react/utils/localeUtils'
import { ERC20DecString, erc20WeiToErc20DecString, toErc20Wei } from '@shared/conversions'
import { logOT } from '@shared/loggingShared'
import { PlanActionEnum } from '@shared/PlanActionCTO'
import { TransactionCTO } from '@shared/TransactionCTO'
import React, { ReactElement, useEffect, useState } from 'react'
import { PlanActionClass } from './PlanActionClass'

enum ExecutionState {
  IDLE = 'idle',
  EXECUTING = 'executing',
  SUCCESS = 'success',
  ERROR = 'error',
}

interface LytActionPlanModalProps {
  planActionClass: PlanActionClass
  onExecute: (modifiedPlanAction: PlanActionClass) => Promise<void>
  onCancel: () => void
}

export default function LytActionPlanModal(props: LytActionPlanModalProps): ReactElement {
  const { planActionClass: planActionClass, onExecute, onCancel } = props
  const originalAmount = erc20WeiToErc20DecString(planActionClass.actualAssets)
  const [amount, setAmount] = useState<string>(originalAmount)
  const [isModified, setIsModified] = useState<boolean>(false)
  const [executionState, setExecutionState] = useState<ExecutionState>(ExecutionState.IDLE)
  const [executionStartTime, setExecutionStartTime] = useState<number>(0)
  const [errorMessage, setErrorMessage] = useState<string>('')

  // Monitor transaction state from Redux
  const _pendingTransaction: TransactionCTO | undefined = useOtSelector(
    (state: OtStoreState) => state.alertState.pendingTransaction
  )

  const liquidityAssetName = planActionClass.nativeLiquidityAsset?.name || 'Unknown Asset'

  const isDeposit = planActionClass.actionEnum === PlanActionEnum.ReserveNativeDeposit
  const isRedeem = planActionClass.actionEnum === PlanActionEnum.RequestReserveRedeem
  const isTransfer = planActionClass.actionEnum === PlanActionEnum.TransferNativeForMarketMakerRedeem
  const isProcessRedemption = planActionClass.actionEnum === PlanActionEnum.ProcessRedemptionForMarketMaker

  const actionType = isDeposit ? 'Deposit' : isRedeem ? 'Redeem' : isTransfer ? 'Transfer' : 'Process Redemption'
  const actionVerb = isDeposit ? 'deposit' : isRedeem ? 'redeem' : isTransfer ? 'transfer' : 'process'
  const executeButtonText = isDeposit
    ? 'Make Deposit'
    : isRedeem
      ? 'Redeem from OT to Sierra Vault'
      : isTransfer
        ? 'Transfer from Sierra Vault to Sierra Pool'
        : 'Process Market Maker Redemption'

  // Handle execution timeout (optional safeguard)
  useEffect(() => {
    if (executionState === ExecutionState.EXECUTING && executionStartTime > 0) {
      const timeout = setTimeout(() => {
        // If still executing after 2 minutes, consider it failed
        if (executionState === ExecutionState.EXECUTING) {
          setExecutionState(ExecutionState.ERROR)
          setErrorMessage('Transaction timed out. Please check your wallet and try again.')
        }
      }, 120000) // 2 minutes

      return () => clearTimeout(timeout)
    }
  }, [executionState, executionStartTime])

  const handleAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value
    setAmount(newValue)

    try {
      const newAmountNum = parseLocaleNumber(newValue)
      const originalAmountNum = parseLocaleNumber(originalAmount)
      setIsModified(!isNaN(newAmountNum) && newAmountNum !== originalAmountNum)
    } catch {
      setIsModified(newValue !== originalAmount)
    }
  }

  const getValidationMessage = () => {
    if (!isModified) return null

    try {
      const currentAmount = parseLocaleNumber(amount)
      const originalAmountNum = parseLocaleNumber(originalAmount)
      if (currentAmount > originalAmountNum) {
        return {
          type: 'error' as const,
          message: `Amount exceeds available funds. Only ${originalAmount} is available for ${actionVerb}.`,
        }
      } else {
        return {
          type: 'warning' as const,
          message: `You are overriding the suggested ${actionVerb} amount from ${originalAmount} to ${amount}. Please ensure this amount is correct.`,
        }
      }
    } catch {
      return null
    }
  }

  const handleExecute = async () => {
    try {
      const modifiedAssets = toErc20Wei(amount as ERC20DecString)

      if (planActionClass.reserve) {
        planActionClass.reserve.actualDepositAmount = BigInt(modifiedAssets)
      }

      logOT.info('LytActionPlanModal: Executing plan action', {
        actionType,
        original: originalAmount,
        modified: amount,
        assets: modifiedAssets,
      })

      // Set executing state immediately
      setExecutionState(ExecutionState.EXECUTING)
      setExecutionStartTime(Date.now())
      setErrorMessage('')

      try {
        logOT.info('LytActionPlanModal: Starting transaction execution and waiting for mining...')
        await onExecute(planActionClass)

        logOT.info('LytActionPlanModal: Transaction completed and mined successfully!')
        setExecutionState(ExecutionState.SUCCESS)

        setTimeout(() => {
          logOT.info('LytActionPlanModal: Closing modal after successful transaction')
          const event = new Event('modalClose')
          window.dispatchEvent(event)
          onCancel()
        }, 2000)
      } catch (executionError) {
        logOT.error('LytActionPlanModal: Transaction execution or mining failed', executionError)
        setExecutionState(ExecutionState.ERROR)
        setErrorMessage(
          executionError instanceof Error
            ? executionError.message
            : 'Transaction failed. Please check your wallet and try again.'
        )
      }
    } catch (error) {
      logOT.error('LytActionPlanModal: Error parsing amount', error)
      setExecutionState(ExecutionState.ERROR)
      setErrorMessage('Failed to parse transaction data. Please check your input and try again.')
    }
  }

  const handleCancel = () => {
    const event = new Event('modalClose')
    window.dispatchEvent(event)
    onCancel()
  }

  const isValidAmount = () => {
    try {
      const amountNum = parseLocaleNumber(amount)
      const originalAmountNum = parseLocaleNumber(originalAmount)
      return amountNum > 0 && !isNaN(amountNum) && amountNum <= originalAmountNum
    } catch {
      return false
    }
  }

  const renderProgressBar = () => (
    <Box sx={{ width: '100%', marginBottom: '24px' }}>
      <Box
        sx={{
          width: '100%',
          height: '4px',
          backgroundColor: theme.palette.background.paper,
          borderRadius: '2px',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            height: '100%',
            backgroundColor: theme.palette.accent1.main,
            animation: 'progress 2s infinite linear',
            '@keyframes progress': {
              '0%': {
                width: '0%',
                marginLeft: '0%',
              },
              '50%': {
                width: '50%',
                marginLeft: '25%',
              },
              '100%': {
                width: '0%',
                marginLeft: '100%',
              },
            },
          }}
        />
      </Box>
    </Box>
  )

  const renderExecutionState = () => {
    switch (executionState) {
      case ExecutionState.EXECUTING:
        return (
          <Box sx={{ textAlign: 'center', padding: 3 }}>
            <PendingIcon sx={{ fontSize: 48, color: theme.palette.accent1.main, marginBottom: 2 }} />
            <Typography variant="h6" gutterBottom>
              Processing Transaction
            </Typography>
            {renderProgressBar()}
            <Typography variant="body2" color="textSecondary">
              Please wait while your transaction is being confirmed and mined...
            </Typography>
            <Typography variant="caption" color="textSecondary" sx={{ marginTop: 1, display: 'block' }}>
              This includes approval, execution, and waiting for blockchain confirmation.
            </Typography>
          </Box>
        )

      case ExecutionState.SUCCESS:
        return (
          <Box sx={{ textAlign: 'center', padding: 3 }}>
            <DoneIcon sx={{ fontSize: 48, color: theme.palette.success.main, marginBottom: 2 }} />
            <Typography variant="h6" gutterBottom color="success.main">
              Transaction Successful
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Your {actionType.toLowerCase()} has been completed successfully.
            </Typography>
          </Box>
        )

      case ExecutionState.ERROR:
        return (
          <Box sx={{ textAlign: 'center', padding: 3 }}>
            <ErrorIcon sx={{ fontSize: 48, color: theme.palette.error.main, marginBottom: 2 }} />
            <Typography variant="h6" gutterBottom color="error.main">
              Transaction Failed
            </Typography>
            <Box
              sx={{
                marginBottom: 2,
                maxHeight: '300px',
                overflowY: 'auto',
                padding: 2,
                backgroundColor: theme.palette.background.default,
                borderRadius: 1,
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{
                  wordBreak: 'break-word',
                  whiteSpace: 'pre-wrap',
                  textAlign: 'left',
                  fontFamily: 'monospace',
                  fontSize: '0.75rem',
                }}
              >
                {errorMessage || 'An error occurred while processing your transaction.'}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              onClick={() => {
                setExecutionState(ExecutionState.IDLE)
                setErrorMessage('')
              }}
              sx={{
                color: theme.palette.fg.main,
                borderColor: theme.palette.fg.main,
              }}
            >
              Try Again
            </Button>
          </Box>
        )

      default:
        return null
    }
  }

  // Show execution state if not idle
  if (executionState !== ExecutionState.IDLE) {
    return <Box sx={{ padding: 2 }}>{renderExecutionState()}</Box>
  }

  return (
    <Box sx={{ padding: 2 }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            {isTransfer
              ? 'Transfer from Sierra Vault to Sierra Pool'
              : isProcessRedemption
                ? 'Process Market Maker Redemption'
                : `Reserve Native ${actionType}`}
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            {isTransfer
              ? 'Configure the transfer amount from Sierra Vault to Sierra Pool'
              : isProcessRedemption
                ? 'Process redemption for market maker and configure the redemption amount'
                : `Configure the ${actionVerb} amount for this reserve pool`}
          </Typography>
          {isDeposit && (
            <Alert severity="info" sx={{ marginTop: 1 }}>
              <Typography variant="body2">
                <strong>Two Transactions Required:</strong> This deposit will require two blockchain transactions:
                <br />
                1. <strong>Approve:</strong> Grant permission for the pool to spend your tokens
                <br />
                2. <strong>Deposit:</strong> Transfer tokens to the pool and mint pool shares
              </Typography>
            </Alert>
          )}
        </Grid>

        {!isTransfer && !isProcessRedemption && (
          <Grid item xs={12}>
            <Typography variant="body1" gutterBottom>
              <strong>Pool:</strong> {planActionClass.reserve?.poolName || 'Unknown Pool'}
            </Typography>
          </Grid>
        )}

        {isProcessRedemption && (
          <Grid item xs={12}>
            <Typography variant="body1" gutterBottom>
              <strong>Market Maker:</strong> {planActionClass.marketMakerAddr || 'Unknown Address'}
            </Typography>
          </Grid>
        )}

        <Grid item xs={12}>
          <Typography variant="body1" gutterBottom>
            <strong>Asset:</strong> {liquidityAssetName}
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <InputLabel htmlFor="action-amount">{actionType} Amount</InputLabel>
          <TextField
            id="action-amount"
            fullWidth
            value={amount}
            onChange={handleAmountChange}
            placeholder={`Enter ${actionVerb} amount`}
            variant="outlined"
            error={!isValidAmount()}
            helperText={
              !isValidAmount()
                ? (() => {
                    try {
                      const amountNum = parseLocaleNumber(amount)
                      const originalAmountNum = parseLocaleNumber(originalAmount)
                      if (amountNum > originalAmountNum) {
                        return `Amount cannot exceed ${originalAmount}`
                      }
                      return 'Please enter a valid positive number'
                    } catch {
                      return 'Please enter a valid positive number'
                    }
                  })()
                : ''
            }
            sx={{ marginTop: 1 }}
          />
        </Grid>

        {(() => {
          const validation = getValidationMessage()
          if (!validation) return null

          return (
            <Grid item xs={12}>
              <Alert severity={validation.type} sx={{ marginTop: 1 }}>
                {validation.message}
              </Alert>
            </Grid>
          )
        })()}

        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, marginTop: 2 }}>
            <Button
              variant="outlined"
              onClick={handleCancel}
              disabled={(executionState as ExecutionState) === ExecutionState.EXECUTING}
              sx={{
                color: theme.palette.fg.main,
                borderColor: theme.palette.fg.main,
                padding: '8px 16px',
                borderRadius: '6px',
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                handleExecute().catch((error) => {
                  logOT.error('LytActionPlanModal: Execute button error', error)
                })
              }}
              disabled={!isValidAmount() || (executionState as ExecutionState) === ExecutionState.EXECUTING}
              startIcon={
                (executionState as ExecutionState) === ExecutionState.EXECUTING ? (
                  <CircularProgress size={16} color="inherit" />
                ) : undefined
              }
              sx={{
                backgroundColor: theme.palette.fg.main,
                color: '#FFFFFF',
                padding: '8px 16px',
                borderRadius: '6px',
                marginTop: 0,
              }}
            >
              {(executionState as ExecutionState) === ExecutionState.EXECUTING ? 'Executing...' : executeButtonText}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  )
}
